import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';

/*
Cart State Shape:
{
  items: Array<{ key, name, baseId, price, quantity }>,
  orientation: 'side' | 'bottom',
  size: number (pixels for width if side, height if bottom),
  isOpen: boolean
}
Persistence: localStorage per facility key: grocery-cart-[facilityId]
Realtime multi-tab sync: storage event + BroadcastChannel fallback
*/

const GroceryCartContext = createContext(null);

const DEFAULT_STATE = {
  items: [],
  orientation: 'bottom',
  size: 400,
  isOpen: false
};

function cartReducer(state, action) {
  switch (action.type) {
    case 'INIT':
      return { ...state, ...action.payload };
    case 'ADD_ITEM': {
      const existing = state.items.find(i => i.key === action.item.key);
      let items;
      if (existing) {
        items = state.items.map(i => i.key === action.item.key ? { ...i, quantity: i.quantity + 1 } : i);
      } else {
        items = [...state.items, { ...action.item, quantity: 1 }];
      }
      return { ...state, items };
    }
    case 'UPDATE_QTY': {
      const items = state.items.map(i => i.key === action.key ? { ...i, quantity: action.quantity } : i).filter(i => i.quantity > 0);
      return { ...state, items };
    }
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter(i => i.key !== action.key) };
    case 'CLEAR':
      return { ...state, items: [] };
    case 'SET_OPEN':
      return { ...state, isOpen: action.isOpen };
    case 'SET_ORIENTATION':
      return { ...state, orientation: action.orientation };
    case 'SET_SIZE':
      return { ...state, size: action.size };
    default:
      return state;
  }
}

export const GroceryCartProvider = ({ facilityId, children }) => {
  // Lazy initializer to avoid flash overwrite between routes
  const storageKey = `grocery-cart-${facilityId}`;
  const lazyInit = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return { ...DEFAULT_STATE, ...JSON.parse(saved) };
    } catch {}
    return DEFAULT_STATE;
  };
  const [state, dispatch] = useReducer(cartReducer, undefined, lazyInit);
  const channelRef = useRef(null);
  const hydratedRef = useRef(true); // already hydrated via lazy init

  // If facilityId changes, rehydrate specifically for that facility
  useEffect(() => {
    const key = `grocery-cart-${facilityId}`;
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        dispatch({ type: 'INIT', payload: JSON.parse(saved) });
        hydratedRef.current = true;
      } else {
        dispatch({ type: 'INIT', payload: DEFAULT_STATE });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  // BroadcastChannel setup
  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      channelRef.current = new BroadcastChannel('grocery-cart');
      channelRef.current.onmessage = (e) => {
        const { facilityId: fId, payload } = e.data || {};
        if (fId === facilityId && payload) {
          dispatch({ type: 'INIT', payload });
        }
      };
      return () => channelRef.current && channelRef.current.close();
    }
  }, [facilityId]);

  // storage event sync
  useEffect(() => {
    const handler = (e) => {
      if (e.key === storageKey && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          dispatch({ type: 'INIT', payload: parsed });
        } catch {}
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [storageKey]);

  // Persist + broadcast
  useEffect(() => {
    const toSave = JSON.stringify(state);
    localStorage.setItem(storageKey, toSave);
    if (channelRef.current) {
      channelRef.current.postMessage({ facilityId, payload: state });
    }
  }, [state, storageKey, facilityId]);

  const addItem = useCallback((item) => {
    dispatch({ type: 'ADD_ITEM', item: { ...item, key: item.baseId || item.name } });
  }, []);

  const updateQuantity = useCallback((key, quantity) => {
    dispatch({ type: 'UPDATE_QTY', key, quantity });
  }, []);

  const removeItem = useCallback((key) => dispatch({ type: 'REMOVE_ITEM', key }), []);
  const clear = useCallback(() => dispatch({ type: 'CLEAR' }), []);
  const setOpen = useCallback((isOpen) => dispatch({ type: 'SET_OPEN', isOpen }), []);
  const setOrientation = useCallback((orientation) => dispatch({ type: 'SET_ORIENTATION', orientation }), []);
  const setSize = useCallback((size) => dispatch({ type: 'SET_SIZE', size }), []);

  const totalItems = state.items.reduce((s, i) => s + i.quantity, 0);
  const totalAmount = state.items.reduce((s, i) => s + (i.price || 0) * i.quantity, 0);

  // Auto orientation for small screens first load
  useEffect(() => {
    if (window.innerWidth < 720 && state.orientation === 'side') {
      dispatch({ type: 'SET_ORIENTATION', orientation: 'bottom' });
      dispatch({ type: 'SET_SIZE', size: 220 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GroceryCartContext.Provider value={{
      ...state,
      addItem,
      updateQuantity,
      removeItem,
      clear,
      setOpen,
      setOrientation,
      setSize,
      totalItems,
      totalAmount
    }}>
      {children}
    </GroceryCartContext.Provider>
  );
};

export const useGroceryCart = () => {
  const ctx = useContext(GroceryCartContext);
  if (!ctx) throw new Error('useGroceryCart must be used within GroceryCartProvider');
  return ctx;
};
